import { ResourceType, TypedResource } from '@alexandrainst/plana-react-api'

export type CachedResourceList<T extends ResourceType> = {
  type: T
  items: TypedResource<T>[]
  currentVersion: number
  sinceVersion: number
}

export type BatchingStrategy = {
  initialSize: number
  remaining:
    | { type: 'bySize'; value: number }
    | { type: 'intoExactly'; value: number }
    | { type: 'intoAtMost'; value: number; minScale: number }
}

type ArrayType<T> = T extends readonly (infer U)[] ? U : never
export type AggregatedQueriesResult<
  QueryResults extends readonly unknown[],
  FlattenKeys extends keyof QueryResults[number] = never,
> = {
  [Key in keyof QueryResults[number]]: Key extends FlattenKeys
    ? ArrayType<
        NonNullable<QueryResults[number][Key]>
      > extends readonly unknown[]
      ? ArrayType<ArrayType<NonNullable<QueryResults[number][Key]>>>[]
      : ArrayType<NonNullable<QueryResults[number][Key]>>[]
    : QueryResults[number][Key][]
}

export const isCacheResourceList = <T extends ResourceType>(
  type: T,
  elm: CachedResourceList<T> | unknown
): elm is CachedResourceList<T> =>
  typeof elm === 'object' &&
  elm !== null &&
  Object.prototype.hasOwnProperty.call(elm, 'type') &&
  (elm as CachedResourceList<T>).type === type

export const detailsCacheUpdater =
  <T extends ResourceType>(newElm: TypedResource<T>) =>
  (oldElm: TypedResource<T> | undefined) =>
    oldElm === undefined || oldElm.data.version < newElm.data.version
      ? newElm
      : oldElm

export const getStatusCodeFromError = (error: unknown): number => {
  if (error instanceof Error) {
    const message = error.message
    const match = message.match(/\[(\d+)\]/)
    if (match && match[1]) {
      return parseInt(match[1], 10)
    }
  }
  return 0
}

export const chunk = <T>(arr: T[], size: number) =>
  Array.from(
    { length: Math.ceil(isFinite(arr.length / size) ? arr.length / size : 0) },
    (_, i) => arr.slice(i * size, i * size + size)
  )

export const distribute = <T>(arr: T[], size: number): T[][] => {
  if (!isFinite(size) || size <= 0) return []

  const baseDistribution = Math.floor(arr.length / size)
  const remainingElements = arr.length % size
  return Array.from({ length: size }, (_, i) => {
    const start = i * baseDistribution + Math.min(i, remainingElements)
    const end = start + baseDistribution + (i < remainingElements ? 1 : 0)
    return arr.slice(start, end)
  })
}

export const batch = <T>(
  arr: T[],
  { initialSize, remaining }: BatchingStrategy
) => {
  const initial = arr.slice(0, initialSize)
  const rest = arr.slice(initialSize)

  switch (remaining.type) {
    case 'bySize':
      return [initial, ...chunk(rest, remaining.value)]
    case 'intoExactly':
      return [initial, ...distribute(rest, remaining.value)]
    case 'intoAtMost':
      return [
        initial,
        ...chunk(
          rest,
          Math.max(
            Math.ceil(rest.length / remaining.value),
            initialSize * remaining.minScale
          )
        ),
      ]
  }
}
